import { FundingPolicyType } from '../../appModel';
import { defaultsRetroactiveWaitingPeriodReimbursement } from '../../FundingPolicyTypeDef';
import type {
  GenworthRateActionLetterMetadata,
  GenworthRateActionOption,
} from './responseSchema';

export interface BuildPolicyFundingSourceFromGenworthOptionOptions {
  advisorId?: string;
  /**
   * Override the option's annualPremium with a different annualized amount. Used to model
   * the synthetic "true original" comparison row, where benefits match KEEP_CURRENT but the
   * premium is `metadata.premiumBeforeIncrease` (the policyholder's pre-increase cost).
   */
  annualPremiumOverride?: number | null;
}

/**
 * Build a PolicyFundingSource from an extracted Genworth rate-action option.
 *
 * Single source of truth used by both:
 *  - the server's Apply mutation (persists a real funding source), and
 *  - the client's projection preview (mutates an in-memory client clone for derivation).
 */
export function buildPolicyFundingSourceFromGenworthOption(
  option: GenworthRateActionOption,
  metadata: GenworthRateActionLetterMetadata,
  { advisorId, annualPremiumOverride }: BuildPolicyFundingSourceFromGenworthOptionOptions = {},
): PolicyFundingSource {
  const dailyMax = option.dailyPaymentMaximum;
  const annualPremium = annualPremiumOverride !== undefined ? annualPremiumOverride : option.annualPremium;
  const monthlyPremium = annualPremium != null ? annualPremium / 12 : null;
  const benefitPeriodMonths = option.benefitPeriodYears != null ? option.benefitPeriodYears * 12 : null;

  // Inflation mapping: PolicyFundingSource only models {isSimpleInflationProtection: bool,
  // policyInflationProtection: number}. The extracted `rate` is a percentage value (e.g. 5
  // for "Compound 5%"), but the calc engine stores `policyInflationProtection` as a decimal
  // fraction (e.g. 0.05) — see calcFundingSourceDerivatives.ts:1405 and the shop-insurance
  // analogue at calcClientROIProjections.ts:127. Without the /100, every option's benefit
  // pool inflates ~100× too fast and all options cap out at the same LTC need.
  // For NONE we explicitly write 0 (rather than null) so the calc treats it as no inflation.
  // GPO (guaranteed purchase option) isn't a percentage rate — approximated as 0 in V1.
  const ip = option.inflationProtection;
  const isSimpleInflation = ip?.type === 'SIMPLE';
  const inflationRate =
    !ip || ip.type === 'NONE' || ip.type === 'GPO' ? 0 : ((ip.rate ?? 0) / 100);

  const policyType = FundingPolicyType.longTermCareInsurance;
  const carrier = metadata.carrier ?? 'Genworth';
  const policyCustomName = `${carrier} — ${option.carrierLabel}`;

  // Cast required because PolicyFundingSource's string-index signature includes Date types
  // we don't use here. Initialize only fields we can derive from the rate action letter,
  // plus the small set of trad-LTC scaffolding the calc engine reads.
  const policy = {
    hasPolicyFunding: true,
    policyType,
    carrierName: carrier,
    customPolicyId: '',
    policyCustomName,
    policyMaximumBenefitAmount: option.remainingLifetimePaymentMaximum,
    policyPremiumMonthlyCost: monthlyPremium,
    policyLumpSumPayment: null,
    // Pre-claim ROI is gated on this being set (calcFundingPolicyPreClaimDerivatives.ts:17);
    // we don't know the policy's true issue year from the letter, so anchor projections at
    // "as if effective this calendar year" — same convention shop-insurance uses for quotes.
    policyPremiumStartYear: new Date().getFullYear(),
    policyBenefitIsUnlimited: false,
    policyBenefitPeriodMonths: benefitPeriodMonths,
    isSimpleInflationProtection: isSimpleInflation,
    policyInflationProtection: inflationRate,
    isIndemnityPolicyPayment: false,
    // Genworth comprehensive LTC pays one Daily Payment Maximum across all care settings;
    // we copy the same value into all three fields. Other carriers may differentiate by
    // setting — when adding a non-Genworth template, this mapping needs to be revisited.
    dailyHomeBenefitAmount: dailyMax,
    dailyHomeInflationProtectionPercent: null,
    dailyAssistedLivingBenefitAmount: dailyMax,
    dailyAssistedLivingInflationProtectionPercent: null,
    dailyNursingHomeBenefitAmount: dailyMax,
    dailyNursingHomeInflationProtectionPercent: null,
    homeCareWaitingPeriodDays: option.homeCareEliminationPeriodDays,
    facilityCareWaitingPeriodDays: option.facilityEliminationPeriodDays,
    hasRetroactiveWaitingPeriodReimbursement: defaultsRetroactiveWaitingPeriodReimbursement(
      policyType,
    )
      ? true
      : null,
    policyAddedAt: Date.now(),
    ...(advisorId && { addedByAdvisorId: advisorId }),
  } as unknown as PolicyFundingSource;

  return policy;
}
